import type { ButtonHTMLAttributes, KeyboardEvent, PropsWithChildren, ReactNode } from "react";
import { AlertTriangle, Inbox, LoaderCircle, X } from "lucide-react";
import { useModalInteraction } from "../modal-interaction";
import { cx } from "../utils";

export function Button({
  variant = "secondary",
  size = "md",
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "icon";
}) {
  return (
    <button className={cx("button", `button-${variant}`, `button-${size}`, className)} {...props}>
      {children}
    </button>
  );
}

export function Badge({ children, tone = "neutral" }: PropsWithChildren<{ tone?: string }>) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

export function Panel({ children, className, interactive = false }: PropsWithChildren<{ className?: string; interactive?: boolean }>) {
  return <section className={cx("panel", interactive && "panel-interactive", className)}>{children}</section>;
}

export function PanelHeader({
  title,
  eyebrow,
  action,
}: {
  title: string;
  eyebrow?: string;
  action?: ReactNode;
}) {
  return (
    <div className="panel-header">
      <div>
        {eyebrow ? <span>{eyebrow}</span> : null}
        <h2>{title}</h2>
      </div>
      {action}
    </div>
  );
}

export function LoadingState({ label = "Mapping the vault…" }: { label?: string }) {
  return (
    <div className="state-block loading-state">
      <LoaderCircle className="spin" size={24} />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="state-block empty-state">
      <div className="state-icon">{icon ?? <Inbox size={22} />}</div>
      <strong>{title}</strong>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="state-block error-state">
      <AlertTriangle size={22} />
      <strong>Something broke the signal.</strong>
      <p>{message}</p>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function Modal({
  title,
  eyebrow,
  children,
  onClose,
  wide = false,
}: PropsWithChildren<{ title: string; eyebrow?: string; onClose: () => void; wide?: boolean }>) {
  const { backdropRef, dialogRef } = useModalInteraction(onClose);
  return (
    <div ref={backdropRef} className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section ref={dialogRef} tabIndex={-1} className={cx("modal", wide && "modal-wide")} role="dialog" aria-modal="true" aria-label={title}>
        <header className="modal-header">
          <div>
            {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
            <h2>{title}</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close dialog">
            <X size={19} />
          </Button>
        </header>
        <div className="modal-body">{children}</div>
      </section>
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: Array<{ value: T; label: string; disabled?: boolean }>;
  onChange: (value: T) => void;
  ariaLabel?: string;
}) {
  const firstEnabled = options.find((option) => !option.disabled)?.value;
  const tabStop = options.some((option) => option.value === value && !option.disabled) ? value : firstEnabled;

  function move(event: KeyboardEvent<HTMLButtonElement>) {
    if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
    const buttons = [...(event.currentTarget.closest('[role="radiogroup"]')
      ?.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)') ?? [])];
    if (!buttons.length) return;
    const current = Math.max(0, buttons.indexOf(event.currentTarget));
    const target = event.key === "Home"
      ? 0
      : event.key === "End"
        ? buttons.length - 1
        : (current + (["ArrowRight", "ArrowDown"].includes(event.key) ? 1 : -1) + buttons.length) % buttons.length;
    const button = buttons[target];
    const option = options.find((candidate) => candidate.value === button?.dataset.value);
    if (!button || !option) return;
    event.preventDefault();
    onChange(option.value);
    button.focus();
  }

  return (
    <div
      className="segmented"
      role="radiogroup"
      aria-label={ariaLabel ?? `Choose ${options.map((option) => option.label).join(" or ")}`}
    >
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          className={option.value === value ? "active" : ""}
          data-value={option.value}
          disabled={option.disabled}
          tabIndex={option.value === tabStop ? 0 : -1}
          onClick={() => onChange(option.value)}
          onKeyDown={move}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function Field({ label, hint, children }: PropsWithChildren<{ label: string; hint?: string }>) {
  return (
    <label className="field">
      <span className="field-label">
        {label}
        {hint ? <small>{hint}</small> : null}
      </span>
      {children}
    </label>
  );
}
